import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  IconButton,
  Link,
  Fade,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import PsychologyIcon from "@mui/icons-material/Psychology";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import ShieldIcon from "@mui/icons-material/Shield";
import { usePageMeta } from "../hooks/usePageMeta";
import { useAuth } from "../AuthContext";

// ── Persistent brand header shown on every slide ─────────────────────

function BrandHeader() {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 3 }}>
      <FavoriteIcon sx={{ fontSize: 20, color: "primary.main" }} />
      <Typography sx={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: "0.85rem", color: "text.primary" }}>
        Seriously
      </Typography>
      <Typography sx={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontStyle: "italic", fontSize: "0.8rem", color: "text.secondary" }}>
        — Find your Person
      </Typography>
    </Box>
  );
}

// ── Sample KY bar chart (static demo data) ───────────────────────────

function SampleBiasChart() {
  const bars = [
    { label: "Character", pct: 100, color: "primary.main" },
    { label: "Attachment", pct: 88, color: "primary.main" },
    { label: "How They Love", pct: 72, color: "text.primary" },
    { label: "Body", pct: 65, color: "text.primary" },
    { label: "Lifestyle", pct: 51, color: "text.disabled" },
    { label: "Career", pct: 30, color: "text.disabled" },
  ];
  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", p: { xs: 1.5, sm: 2 }, backgroundColor: "background.default", borderRadius: 0.5 }}>
      {bars.map((b) => (
        <Box key={b.label} sx={{ mb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.72rem", fontWeight: 500 }}>{b.label}</Typography>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.65rem", color: "text.disabled" }}>{b.pct}%</Typography>
          </Box>
          <Box sx={{ height: 3, backgroundColor: "divider", overflow: "hidden" }}>
            <Box sx={{ height: "100%", width: `${b.pct}%`, backgroundColor: b.color }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ── Slide components ─────────────────────────────────────────────────

function SlideHero() {
  return (
    <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
      <FavoriteIcon sx={{ fontSize: { xs: 48, sm: 56 }, color: "primary.main", mb: 2 }} />
      <Typography variant="h3" sx={{ mb: 1.5, fontSize: { xs: "2rem", sm: "2.8rem" } }}>
        Seriously
      </Typography>
      <Typography sx={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontStyle: "italic", fontSize: { xs: "1rem", sm: "1.15rem" }, color: "text.secondary", lineHeight: 1.6, mb: 3, maxWidth: 400, mx: "auto" }}>
        Find your Person
      </Typography>
      <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.75rem", color: "text.disabled", mt: 1 }}>
        A free dating app built to be the opposite of everything wrong with dating.
      </Typography>
      <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", color: "text.disabled", mt: 0.5 }}>
        Non-profit. No investors. No incentive to keep you swiping.
      </Typography>
    </Box>
  );
}

function SlideProblem() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <BrandHeader />
      <Typography variant="caption" sx={{ color: "primary.main", mb: 2, display: "block" }}>The problem</Typography>
      <Typography variant="h5" sx={{ mb: 2, fontSize: { xs: "1.15rem", sm: "1.35rem" } }}>
        Dating apps are designed to keep you single.
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        They make money when you swipe, not when you meet. They hide your best matches behind paywalls. They gamify loneliness into an engagement loop.
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        The industry calls this a "credit economy." It runs on the same psychology as mobile gaming — variable reward, manufactured scarcity, the sunk cost of time already spent.
      </Typography>
      <Link
        href="/the-economy-of-loneliness.html"
        sx={{ fontFamily: "'DM Sans'", fontSize: "0.8rem", color: "primary.main", textDecoration: "none", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}
      >
        Read the full essay →
      </Link>
    </Box>
  );
}

function SlideVoice() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <BrandHeader />
      <Typography variant="caption" sx={{ color: "primary.main", mb: 2, display: "block" }}>Our approach</Typography>
      <Typography variant="h5" sx={{ mb: 3, fontSize: { xs: "1.15rem", sm: "1.35rem" } }}>
        Voice-first. Depth over volume.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, sm: 4 } }}>
        {[
          { icon: <RecordVoiceOverIcon sx={{ fontSize: 22, color: "primary.main" }} />, title: "Voice, not text", body: "You hear warmth, humour, hesitation, confidence — everything that matters and nothing a chatbot can replicate." },
          { icon: <VolunteerActivismIcon sx={{ fontSize: 22, color: "primary.main" }} />, title: "Five conversations. Not fifty.", body: "When you're not juggling a dozen chats, you're actually present. And when you're present, real connections happen." },
          { icon: <MoneyOffIcon sx={{ fontSize: 22, color: "primary.main" }} />, title: "No paywalls. No tricks.", body: "Free. Not freemium. No hidden features locked behind a subscription." },
        ].map((p) => (
          <Box key={p.title} sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <Box sx={{ flexShrink: 0, mt: 0.3 }}>{p.icon}</Box>
            <Box>
              <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.9rem", fontWeight: 500, color: "text.primary", mb: 0.3 }}>{p.title}</Typography>
              <Typography variant="body2">{p.body}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function SlideDifferentiators() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <BrandHeader />
      <Typography variant="caption" sx={{ color: "primary.main", mb: 2, display: "block" }}>What makes this different</Typography>
      <Typography variant="h5" sx={{ mb: 3, fontSize: { xs: "1.15rem", sm: "1.35rem" } }}>
        Built around what actually matters.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 3 }}>
        {[
          { title: "Fair attention for everyone.", body: "Everyone gets a fair chance at being seen — not just the people the algorithm thinks are popular." },
          { title: "Endings matter.", body: "When a conversation ends, both people leave a farewell message and a reflection. Not a rating — a moment of kindness." },
          { icon: <ShieldIcon sx={{ fontSize: 22, color: "primary.main" }} />, title: "Your data stays yours.", body: "No selling, no dark patterns, no tricks. Privacy is the default." },
        ].map((d) => (
          <Box key={d.title}>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.9rem", fontWeight: 500, color: "text.primary", mb: 0.3 }}>{d.title}</Typography>
            <Typography variant="body2">{d.body}</Typography>
          </Box>
        ))}
      </Box>
      <Link
        component={RouterLink}
        to="/manifesto"
        sx={{ fontFamily: "'DM Sans'", fontSize: "0.8rem", color: "primary.main", textDecoration: "none", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}
      >
        Read our manifesto →
      </Link>
    </Box>
  );
}

function SlideKnowYourself() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <BrandHeader />
      <Typography variant="caption" sx={{ color: "primary.main", mb: 2, display: "block" }}>Before you start</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <PsychologyIcon sx={{ fontSize: 24, color: "primary.main" }} />
        <Typography variant="h5" sx={{ fontSize: { xs: "1.15rem", sm: "1.35rem" } }}>
          Know Yourself
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        An in-depth analysis of what actually drives your dating choices — not what you think you want. Forced trade-offs reveal what surveys can't.
      </Typography>

      {/* Sample findings visual */}
      <Typography variant="caption" sx={{ color: "text.disabled", mb: 1, display: "block" }}>Sample result</Typography>
      <SampleBiasChart />

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mt: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.6rem", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: "primary.main", mb: 0.5 }}>Drawn to</Typography>
          {["Quietly confident", "Reliable and consistent", "Intellectually stimulating"].map((t) => (
            <Typography key={t} sx={{ fontFamily: "'DM Sans'", fontSize: "0.72rem", color: "text.primary", mb: 0.3 }}>{t}</Typography>
          ))}
        </Box>
        <Box>
          <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.6rem", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: "text.disabled", mb: 0.5 }}>Turns off</Typography>
          {["Heavy drinker", "Hot and cold", "Wants children"].map((t) => (
            <Typography key={t} sx={{ fontFamily: "'DM Sans'", fontSize: "0.72rem", color: "text.disabled", mb: 0.3 }}>{t}</Typography>
          ))}
        </Box>
      </Box>

      <Button
        variant="contained"
        size="large"
        href="https://know-yourself.dateseriously.com"
        target="_blank"
        rel="noopener noreferrer"
        sx={{ alignSelf: "flex-start", px: 4 }}
      >
        Play Know Yourself
      </Button>
    </Box>
  );
}

function SlideHowItWorks() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
      <BrandHeader />
      <Typography variant="caption" sx={{ color: "primary.main", mb: 4, display: "block" }}>How to get started</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 3.5, sm: 4 } }}>
        {[
          { step: "01", title: "Play Know Yourself", body: "Discover the unconscious biases shaping your dating choices. Free, no account needed to start." },
          { step: "02", title: "Create your account", body: "Join the waitlist for your area. We'll text you the moment we go live." },
          { step: "03", title: "Complete your profile", body: "Upload your photo, record your voice, set your location. Complete profiles go live first." },
          { step: "04", title: "We'll text you", body: "When we have enough people in your area, we go live — and you'll be first to start matching." },
        ].map((s) => (
          <Box key={s.step} sx={{ display: "flex", gap: { xs: 2, sm: 3 } }}>
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", fontWeight: 500, letterSpacing: "0.08em", color: "primary.main", pt: 0.3, flexShrink: 0 }}>{s.step}</Typography>
            <Box>
              <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.9rem", fontWeight: 500, color: "text.primary", mb: 0.3 }}>{s.title}</Typography>
              <Typography variant="body2">{s.body}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function SlideCTA({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { isAuthenticated } = useAuth();
  const [urlId, setUrlId] = useState("");

  // ── Logged-in user: welcome back ──
  if (isAuthenticated) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
        <BrandHeader />
        <Typography variant="h5" sx={{ fontSize: { xs: "1.15rem", sm: "1.35rem" }, mb: 1 }}>
          Welcome back.
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 4, maxWidth: 380 }}>
          Continue setting up your profile, or play Know Yourself to discover your dating biases.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%", maxWidth: 320 }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => onNavigate("/profile")}
          >
            Go to Profile
          </Button>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            href="https://know-yourself.dateseriously.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ color: "primary.main", borderColor: "primary.main", "&:hover": { borderColor: "primary.dark", backgroundColor: "rgba(192,57,43,0.04)" } }}
          >
            Play Know Yourself
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Not logged in: sign-up flow ──
  return (
    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center" }}>
      <BrandHeader />
      <Typography variant="h5" sx={{ fontSize: { xs: "1.15rem", sm: "1.35rem" }, mb: 1 }}>
        Ready to find your person?
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 4, maxWidth: 380 }}>
        Seriously is in pre-launch. Join the waitlist now — we'll text you the moment we go live in your area.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%", maxWidth: 320, mb: 4 }}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={() => onNavigate("/create-account")}
          sx={{ py: 1.5 }}
        >
          Join the Waitlist
        </Button>
        <Button
          variant="outlined"
          size="large"
          fullWidth
          href="https://know-yourself.dateseriously.com"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: "primary.main", borderColor: "primary.main", "&:hover": { borderColor: "primary.dark", backgroundColor: "rgba(192,57,43,0.04)" } }}
        >
          Play Know Yourself first
        </Button>
      </Box>

      <Box sx={{ width: "100%", maxWidth: 320, textAlign: "left" }}>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1, textAlign: "center", fontSize: "0.8rem" }}>
          Already have a private URL?
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g. abc123xyz456"
            value={urlId}
            onChange={(e) => setUrlId(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && urlId.trim()) onNavigate(`/u/${urlId.trim()}`); }}
          />
          <Button
            variant="outlined"
            size="small"
            disabled={!urlId.trim()}
            onClick={() => onNavigate(`/u/${urlId.trim()}`)}
          >
            Go
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

// ── Carousel ─────────────────────────────────────────────────────────

const SLIDES = [
  { key: "hero", component: SlideHero },
  { key: "problem", component: SlideProblem },
  { key: "voice", component: SlideVoice },
  { key: "diff", component: SlideDifferentiators },
  { key: "ky", component: SlideKnowYourself },
  { key: "how", component: SlideHowItWorks },
  { key: "cta", component: SlideCTA },
];

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  usePageMeta(
    "Seriously — Find your Person",
    "Seriously — discover your unconscious dating biases with Know Yourself, then find your person. Voice-first dating, no paywalls, no tricks.",
  );

  const go = (dir: 1 | -1) => {
    setCurrent((c) => Math.max(0, Math.min(SLIDES.length - 1, c + dir)));
  };

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") go(1);
      if (e.key === "ArrowLeft" || e.key === "ArrowUp") go(-1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Touch swipe
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
      go(dx < 0 ? 1 : -1);
    }
  };

  // Scroll to top on slide change
  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [current]);

  const SlideComponent = SLIDES[current].component;
  const isFirst = current === 0;
  const isLast = current === SLIDES.length - 1;

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Slide content */}
      <Box
        ref={containerRef}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        sx={{ flex: 1, overflow: "auto", backgroundColor: "secondary.main" }}
      >
        <Container maxWidth="sm">
          <Box sx={{ py: { xs: 3, sm: 5 }, minHeight: "calc(100vh - 120px)", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Fade in timeout={400} key={current}>
              <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
                <CardContent sx={{ p: { xs: 2.5, sm: 4 }, "&:last-child": { pb: { xs: 2.5, sm: 4 } } }}>
                  <SlideComponent onNavigate={(path: string) => navigate(path)} />
                </CardContent>
              </Card>
            </Fade>
            {/* Slide counter */}
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.65rem", color: "text.disabled", textAlign: "center", mt: 2 }}>
              {current + 1} / {SLIDES.length}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Navigation bar */}
      <Box sx={{ borderTop: "1px solid", borderColor: "divider", backgroundColor: "background.default", py: 1.5, position: "sticky", bottom: 0 }}>
        <Container maxWidth="sm">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {/* Back */}
            <IconButton onClick={() => go(-1)} disabled={isFirst} size="small" sx={{ color: "text.disabled", "&:disabled": { opacity: 0.3 } }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>

            {/* Dots */}
            <Box sx={{ display: "flex", gap: 0.8, alignItems: "center" }}>
              {SLIDES.map((s, i) => (
                <Box
                  key={s.key}
                  onClick={() => setCurrent(i)}
                  sx={{
                    width: i === current ? 18 : 6,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: i === current ? "primary.main" : "divider",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </Box>

            {/* Forward / CTA */}
            {isLast ? (
              <Button variant="contained" size="small" onClick={() => navigate("/create-account")} sx={{ fontSize: "0.7rem", px: 2 }}>
                Join
              </Button>
            ) : (
              <IconButton onClick={() => go(1)} size="small" sx={{ color: "primary.main" }}>
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Container>
      </Box>

      {/* Footer — only on first slide */}
      {isFirst && (
        <Box sx={{ maxWidth: 560, mx: "auto", px: 2, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <Typography sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", color: "text.disabled" }}>
            &copy; Seriously
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Link component={RouterLink} to="/manifesto" sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", color: "text.disabled", textDecoration: "none", "&:hover": { color: "primary.main" } }}>
              Manifesto
            </Link>
            <Link component={RouterLink} to="/privacy" sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", color: "text.disabled", textDecoration: "none", "&:hover": { color: "primary.main" } }}>
              Privacy
            </Link>
            <Link component={RouterLink} to="/terms" sx={{ fontFamily: "'DM Sans'", fontSize: "0.7rem", color: "text.disabled", textDecoration: "none", "&:hover": { color: "primary.main" } }}>
              Terms
            </Link>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Landing;
